import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { IconChevronRight } from "@tabler/icons-react";

import MetaLogo from "@/assets/meta-logo.svg?react";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { ActionDialog, DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { AsideRow, Field, SettingsGrid, UnsavedBar } from "@/components/settings/settings-kit";
import { buildMetaAdsManagerUrl } from "@/components/brand/MetaLaunchPausedNotice";
import {
  disconnectMetaAds,
  fetchAdAccounts,
  fetchFacebookPages,
  getMetaAdsStatus,
  getMetaOauthUrl,
  type MetaAdsStatus,
  setDefaultAdAccount,
  setDefaultFacebookPage,
  setMetaConversionsPixel,
} from "@/services/metaAds.service";
import { formatDate } from "@/utils/formatters";
import RunMetaCampaignTab from "./RunMetaCampaignTab";
import { rowsOf, shortId } from "./insights";

type Named = { id: string; name?: string };
type Tab = "overview" | "launch";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function namedRows(res: unknown): Named[] {
  return rowsOf(res)
    .map((r) => ({ id: String(r.id ?? ""), name: typeof r.name === "string" ? r.name : undefined }))
    .filter((r) => r.id);
}

function nameOf(list: Named[], id: string | null | undefined) {
  if (!id) return null;
  return list.find((x) => x.id === id)?.name ?? null;
}

/** A select's footprint while its options load, so the form does not jump. */
function PendingSelect({ label }: { label: string }) {
  return (
    <div className="flex h-9 w-full items-center rounded-lg border border-input px-3 text-sm text-muted-foreground opacity-60">
      {label}
    </div>
  );
}

export default function MetaAdsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("overview");
  const [status, setStatus] = useState<MetaAdsStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const [accounts, setAccounts] = useState<Named[]>([]);
  const [pages, setPages] = useState<Named[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [draft, setDraft] = useState({ accountId: "", pageId: "", pixelId: "" });
  const [saving, setSaving] = useState(false);

  const connected = Boolean(status?.connected);
  const accountId = status?.defaultAdAccountId?.trim() || "";
  const needsDefault = connected && !accountId;
  const gated = !connected || needsDefault;

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      setStatus(await getMetaAdsStatus());
    } catch (e) {
      toast.error(errMessage(e));
      setStatus({
        connected: false,
        deauthorized: false,
        metaUserId: null,
        defaultAdAccountId: null,
        defaultFacebookPageId: null,
        metaPixelId: null,
        tokenExpiresAt: null,
      });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const oauth = searchParams.get("meta_oauth");
    if (!oauth) return;
    if (oauth === "success") {
      toast.success("Meta connected");
      void loadStatus();
    } else if (oauth === "denied") {
      toast.error("Meta authorization was denied");
    } else {
      toast.error("Could not connect Meta");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("meta_oauth");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, loadStatus]);

  useEffect(() => {
    if (gated && tab !== "overview") setTab("overview");
  }, [gated, tab]);

  // Accounts and pages, once connected.
  useEffect(() => {
    if (!connected) {
      setAccounts([]);
      setPages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setListsLoading(true);
      try {
        const [a, p] = await Promise.all([fetchAdAccounts(), fetchFacebookPages()]);
        if (!cancelled) {
          setAccounts(namedRows(a));
          setPages(namedRows(p));
        }
      } catch (e) {
        if (!cancelled) toast.error(errMessage(e));
      } finally {
        if (!cancelled) setListsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connected]);

  const fromStatus = useCallback(
    () => ({
      accountId: status?.defaultAdAccountId ?? "",
      pageId: status?.defaultFacebookPageId ?? "",
      pixelId: status?.metaPixelId ?? "",
    }),
    [status]
  );
  useEffect(() => setDraft(fromStatus()), [fromStatus]);

  const original = fromStatus();
  const hasChanges =
    draft.accountId !== original.accountId ||
    draft.pageId !== original.pageId ||
    draft.pixelId.replace(/\D/g, "") !== (original.pixelId || "");

  const saveDefaults = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    setSaving(true);
    try {
      if (draft.accountId !== original.accountId) {
        if (!draft.accountId) throw new Error("Choose an ad account");
        await setDefaultAdAccount(draft.accountId);
      }
      if (draft.pageId !== original.pageId) {
        if (!draft.pageId) throw new Error("Choose a Facebook page");
        await setDefaultFacebookPage(draft.pageId);
      }
      const pixel = draft.pixelId.replace(/\D/g, "");
      if (pixel !== (original.pixelId || "")) await setMetaConversionsPixel(pixel);
      toast.success("Meta settings saved");
      await loadStatus();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { url } = await getMetaOauthUrl();
      window.location.href = url;
    } catch (e) {
      toast.error(errMessage(e));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectMetaAds();
      toast.success("Meta disconnected");
      setConfirmDisconnect(false);
      setTab("overview");
      await loadStatus();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setDisconnecting(false);
    }
  };

  const accountName = nameOf(accounts, accountId);
  const pageName = nameOf(pages, status?.defaultFacebookPageId);
  const adsManagerUrl = buildMetaAdsManagerUrl(accountId);

  const tabs = [
    { value: "overview" as const, label: "Overview" },
    { value: "launch" as const, label: "Launch", disabled: gated },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Meta Ads"
        customActions={
          connected
            ? [
                {
                  id: "open-ads-manager",
                  label: "Open Ads Manager",
                  onClick: () => window.open(adsManagerUrl, "_blank", "noopener,noreferrer"),
                },
              ]
            : []
        }
      />

      <UnderlineTabs
        tabs={tabs}
        value={tab}
        onValueChange={setTab}
        onDisabledClick={() =>
          toast.error(!connected ? "Connect Meta first" : "Choose a default ad account first")
        }
        accent="brand"
      />

      {tab === "overview" && (
        <>
          {statusLoading ? (
            <div className="rounded-lg border border-border bg-card">
              <div className="flex items-center gap-4 px-5 py-5 animate-pulse">
                <div className="size-12 rounded-md bg-muted" />
                <div className="space-y-2">
                  <div className="h-4 w-48 rounded bg-muted" />
                  <div className="h-3 w-64 rounded bg-muted" />
                </div>
              </div>
            </div>
          ) : !connected ? (
            <DashboardCard title="Connection">
              {status?.deauthorized && (
                <div className="px-5 pb-4">
                  <DialogNotice tone="warning">
                    Meta revoked this connection. Reconnect to manage ads again. Sales, earnings and payouts are unchanged.
                  </DialogNotice>
                </div>
              )}
              <EmptyState
                bordered={false}
                icon={<MetaLogo className="size-5" />}
                title="Connect your Meta ad account"
                body="Launch ads from approved affiliate content and see ad spend beside affiliate sales"
                action={
                  <Button onClick={() => void handleConnect()} disabled={connecting}>
                    {connecting ? "Opening Meta" : "Connect Meta"}
                  </Button>
                }
              />
            </DashboardCard>
          ) : (
            <>
              <DashboardCard title="Account" to="/brand/meta-ads/performance">
                <div className="flex flex-wrap items-center gap-4 px-5 pb-4">
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-card">
                    <MetaLogo className="size-6" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-[15px] font-semibold text-foreground">
                        {accountName ?? (accountId ? shortId(accountId) : "No ad account yet")}
                      </span>
                      <StatusText status={needsDefault ? "PENDING" : "ACTIVE"} />
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {needsDefault
                        ? "Choose a default ad account below to unlock launching and performance"
                        : `Ad account ${accountId}${pageName ? ` · Page ${pageName}` : ""}`}
                    </div>
                  </div>
                  {!needsDefault && (
                    <Link
                      to="/brand/meta-ads/performance"
                      className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 underline-offset-4 hover:underline"
                    >
                      Spend, ROAS and creators on Ad Performance
                      <IconChevronRight size={16} stroke={1.75} aria-hidden />
                    </Link>
                  )}
                </div>
              </DashboardCard>

              <form onSubmit={saveDefaults}>
                <SettingsGrid
                  form={
                    <DashboardCard title="Defaults" className="lg:h-full" bodyClassName="space-y-5 px-5 pb-5">
                      <Field label="Ad account" htmlFor="meta-account" trailing="Where campaigns are created and read from">
                        {/* The select mounts only once its options exist. A
                            value that arrives before its option makes the
                            hidden native select report an empty change, which
                            would wipe the draft. Empty changes are ignored too. */}
                        {accounts.length === 0 ? (
                          <PendingSelect label={listsLoading ? "Loading accounts" : "No ad accounts on this Meta user"} />
                        ) : (
                          <Select
                            value={draft.accountId}
                            onValueChange={(v) => v && setDraft((d) => ({ ...d, accountId: v }))}
                          >
                            <SelectTrigger id="meta-account">
                              <SelectValue placeholder="Choose an ad account" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {accounts.map((a) => (
                                <SelectItem key={a.id} value={a.id}>
                                  {a.name ? `${a.name} (${shortId(a.id)})` : a.id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <Field label="Facebook page" htmlFor="meta-page" trailing="The page ads are published as">
                        {pages.length === 0 ? (
                          <PendingSelect label={listsLoading ? "Loading pages" : "No pages on this Meta user"} />
                        ) : (
                          <Select
                            value={draft.pageId}
                            onValueChange={(v) => v && setDraft((d) => ({ ...d, pageId: v }))}
                          >
                            <SelectTrigger id="meta-page">
                              <SelectValue placeholder="Choose a page" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {pages.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name ? `${p.name} (${shortId(p.id)})` : p.id}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </Field>
                      <Field
                        label="Pixel"
                        htmlFor="meta-pixel"
                        trailing="Optional, sends purchases to the Conversions API"
                      >
                        <Input
                          id="meta-pixel"
                          inputMode="numeric"
                          value={draft.pixelId}
                          onChange={(e) => setDraft((d) => ({ ...d, pixelId: e.target.value }))}
                          placeholder="Pixel id"
                          className="tabular-nums"
                        />
                      </Field>
                    </DashboardCard>
                  }
                  aside={
                    <>
                      <DashboardCard title="Connection" bodyClassName="px-5 pb-3">
                        <AsideRow label="Status">
                          <StatusText status={status?.deauthorized ? "SUSPENDED" : "ACTIVE"} />
                        </AsideRow>
                        <AsideRow label="Meta user">{shortId(status?.metaUserId)}</AsideRow>
                        <AsideRow label="Ad account">{accountName ?? shortId(accountId) ?? "Not set"}</AsideRow>
                        <AsideRow label="Page">{pageName ?? (status?.defaultFacebookPageId ? shortId(status.defaultFacebookPageId) : "Not set")}</AsideRow>
                        <AsideRow label="Pixel">{status?.metaPixelId ? shortId(status.metaPixelId) : "Off"}</AsideRow>
                        {status?.tokenExpiresAt && (
                          <AsideRow label="Access expires">{formatDate(status.tokenExpiresAt)}</AsideRow>
                        )}
                        <div className="flex items-center justify-end gap-3 border-t border-border pt-3">
                          <button
                            type="button"
                            onClick={() => setConfirmDisconnect(true)}
                            className="text-xs font-medium text-destructive underline-offset-4 hover:underline"
                          >
                            Disconnect
                          </button>
                        </div>
                      </DashboardCard>
                      <UnsavedBar visible={hasChanges} saving={saving} onCancel={() => setDraft(fromStatus())} />
                    </>
                  }
                />
              </form>
            </>
          )}
        </>
      )}

      {tab === "launch" && (
        <RunMetaCampaignTab
          pages={pages}
          pagesLoading={listsLoading}
          defaultFacebookPageId={status?.defaultFacebookPageId ?? ""}
          defaultAdAccountId={accountId}
          metaPixelId={status?.metaPixelId ?? null}
        />
      )}

      <ActionDialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        title="Disconnect Meta?"
        description="Launching and campaign data stop until you reconnect."
        footer={
          <>
            <DialogTextButton onClick={() => setConfirmDisconnect(false)} disabled={disconnecting}>
              Cancel
            </DialogTextButton>
            <Button type="button" variant="destructive" onClick={() => void handleDisconnect()} disabled={disconnecting}>
              Disconnect
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          Ads already created in Meta keep running. Affiliate sales, earnings and payouts are unchanged.
        </p>
      </ActionDialog>
    </div>
  );
}
